import type { ActivityDTO } from 'src/types/dto';
import type { MbscCalendarColor } from '@mobiscroll/react';

import dayjs, { type Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import { UTC_TIMEZONE } from 'src/utils/timezone';
import {
  toMobiscrollWeekDays,
  DEFAULT_NON_WORKING_DAYS,
  type NonWorkingDaysResolver,
} from 'src/utils/working-days';

dayjs.extend(utc);
dayjs.extend(timezone);

export type MutedDayMobiscrollColorStyle = Pick<MbscCalendarColor, 'background' | 'textColor'> & {
  cssClass: string;
};

function activityDateOnlyString(value: string | Date | null | undefined): string | null {
  if (value == null) {
    return null;
  }
  if (value instanceof Date) {
    return dayjs.utc(value).format('YYYY-MM-DD');
  }
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  const iso = trimmed.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    return iso;
  }
  const parsed = dayjs(trimmed);
  return parsed.isValid() ? parsed.utc().format('YYYY-MM-DD') : null;
}

function parseActivityDay(
  value: string | Date | undefined | null,
  timezoneId: string | null | undefined
): Dayjs | null {
  if (value == null || value === '') return null;
  const dateStr = activityDateOnlyString(value);
  if (!dateStr) return null;
  const tz = timezoneId?.trim() || UTC_TIMEZONE.id;
  const parsed = dayjs.tz(dateStr, 'YYYY-MM-DD', tz);
  return parsed.isValid() ? parsed.startOf('day') : null;
}

export function resolveActivitySiteTimezoneId(
  activity: ActivityDTO | null | undefined,
  fallbackTimezoneId: string | null | undefined = UTC_TIMEZONE.id
): string {
  return (
    activity?.site?.timezoneId?.trim() || fallbackTimezoneId?.trim() || UTC_TIMEZONE.id
  );
}

export function planboxSiteTimezoneForActivity(
  activity: ActivityDTO | null | undefined
): string | null {
  return activity?.site?.timezoneId?.trim() || null;
}

/** `undefined` means no auto-switch is pending restore. */
export type PlanboxTimezoneRestoreValue = string | null | undefined;

export function shouldSwitchTimezoneForPlanboxSelection(
  siteTimezoneId: string | null,
  displayTimezone: string
): boolean {
  return Boolean(siteTimezoneId && siteTimezoneId !== displayTimezone);
}

export interface ActivityAuditWindow {
  /** Inclusive window start (start of day in `timezoneId`). */
  start: Dayjs;
  /** Inclusive window end (start of day in `timezoneId`). */
  end: Dayjs;
  /** IANA zone for the activity site — audit dates are calendar days here. */
  timezoneId: string;
}

/** Inclusive audit window from activity `startDate` / `dueDate` in the site timezone. */
export function activityAuditWindowFromActivity(
  activity: ActivityDTO | null | undefined,
  fallbackTimezoneId: string | null | undefined = UTC_TIMEZONE.id
): ActivityAuditWindow | null {
  if (!activity) return null;
  const timezoneId = resolveActivitySiteTimezoneId(activity, fallbackTimezoneId);
  const range = activityDateRange(activity, timezoneId);
  if (!range) return null;
  return { ...range, timezoneId };
}

export const ACTIVITY_AUDIT_WINDOW_DATE_FORMAT = 'DD MMM YYYY';

/** Calendar-day audit window labels for tooltips (site timezone). */
export function formattedActivityAuditWindow(
  activity: ActivityDTO | null | undefined,
  fallbackTimezoneId: string | null | undefined = UTC_TIMEZONE.id
): { start: string; end: string } | null {
  const auditWindow = activityAuditWindowFromActivity(activity, fallbackTimezoneId);
  if (!auditWindow) return null;
  return {
    start: auditWindow.start.format(ACTIVITY_AUDIT_WINDOW_DATE_FORMAT),
    end: auditWindow.end.format(ACTIVITY_AUDIT_WINDOW_DATE_FORMAT),
  };
}

export function isDisplayDayOutsideAuditWindow(
  displayDay: Dayjs,
  auditWindow: ActivityAuditWindow
): boolean {
  // Compare the timeline column's calendar label against the audit window using site-timezone
  // calendar days. Instant overlap across zones incorrectly keeps the day before window start
  // unmuted when the site zone is ahead of the display zone (e.g. 30 Jul display vs 31 Jul start).
  const displayDateStr = displayDay.format('YYYY-MM-DD');
  const siteCalendarDay = dayjs.tz(displayDateStr, 'YYYY-MM-DD', auditWindow.timezoneId);
  const windowStartStr = auditWindow.start.format('YYYY-MM-DD');
  const windowEndStr = auditWindow.end.format('YYYY-MM-DD');
  const siteDateStr = siteCalendarDay.format('YYYY-MM-DD');
  return siteDateStr < windowStartStr || siteDateStr > windowEndStr;
}

/**
 * Per-day Mobiscroll colors for timeline columns outside the audit window.
 * `displayTimezone` is the timeline picker zone; window bounds use the site zone.
 */
export function outsideAuditWindowDayColors(
  auditWindow: ActivityAuditWindow,
  displayTimezone: string | null | undefined,
  visibleStart: Date,
  visibleEnd: Date,
  title: string,
  mutedDayStyle: MutedDayMobiscrollColorStyle
): MbscCalendarColor[] {
  const displayTz = displayTimezone?.trim() || UTC_TIMEZONE.id;
  const colors: MbscCalendarColor[] = [];

  let cursor = dayjs(visibleStart).tz(displayTz).startOf('day');
  const last = dayjs(visibleEnd).tz(displayTz).startOf('day');

  while (!cursor.isAfter(last, 'day')) {
    if (isDisplayDayOutsideAuditWindow(cursor, auditWindow)) {
      colors.push({
        ...mutedDayStyle,
        date: cursor.toDate(),
        title,
      });
    }
    cursor = cursor.add(1, 'day');
  }

  return colors;
}

export interface ActivityBlackoutRange {
  start: Dayjs;
  end: Dayjs;
}

/** Expand annual scheme blackout ranges across years overlapping the activity audit window. */
export function activityBlackoutRangesFromActivity(
  activity: ActivityDTO | null | undefined,
  fallbackTimezoneId: string = UTC_TIMEZONE.id
): ActivityBlackoutRange[] {
  if (!activity?.blackoutDates?.length) {
    return [];
  }

  const auditWindow = activityAuditWindowFromActivity(activity, fallbackTimezoneId);
  if (!auditWindow) {
    return [];
  }

  const startYear = auditWindow.start.year();
  const endYear = auditWindow.end.year();
  const ranges: ActivityBlackoutRange[] = [];

  for (const blackout of activity.blackoutDates) {
    if (!blackout.startDate && !blackout.endDate) {
      continue;
    }

    for (let year = startYear; year <= endYear; year++) {
      const rangeStart = blackout.startDate
        ? dayjs(blackout.startDate).year(year).startOf('day')
        : null;
      const rangeEnd = blackout.endDate
        ? dayjs(blackout.endDate).year(year).startOf('day')
        : null;

      if (rangeStart && rangeEnd) {
        if (rangeStart.isAfter(rangeEnd)) {
          ranges.push({ start: rangeStart, end: rangeStart.endOf('year') });
          ranges.push({ start: rangeEnd.startOf('year'), end: rangeEnd });
        } else {
          ranges.push({ start: rangeStart, end: rangeEnd });
        }
      } else if (rangeStart) {
        ranges.push({ start: rangeStart, end: rangeStart });
      } else if (rangeEnd) {
        ranges.push({ start: rangeEnd, end: rangeEnd });
      }
    }
  }

  return ranges;
}

export function isDisplayDayInBlackoutRange(
  displayDay: Dayjs,
  blackoutRanges: ActivityBlackoutRange[],
  auditWindow: ActivityAuditWindow
): boolean {
  if (isDisplayDayOutsideAuditWindow(displayDay, auditWindow)) {
    return false;
  }

  const displayDateStr = displayDay.format('YYYY-MM-DD');
  const siteCalendarDay = dayjs.tz(displayDateStr, 'YYYY-MM-DD', auditWindow.timezoneId);
  const siteDateStr = siteCalendarDay.format('YYYY-MM-DD');

  return blackoutRanges.some((range) => {
    const startStr = range.start.format('YYYY-MM-DD');
    const endStr = range.end.format('YYYY-MM-DD');
    return siteDateStr >= startStr && siteDateStr <= endStr;
  });
}

export function blackoutDayColors(
  auditWindow: ActivityAuditWindow,
  blackoutRanges: ActivityBlackoutRange[],
  displayTimezone: string,
  visibleStart: Date,
  visibleEnd: Date,
  title: string,
  blackoutDayStyle: MutedDayMobiscrollColorStyle
): MbscCalendarColor[] {
  if (blackoutRanges.length === 0) {
    return [];
  }

  const displayTz = displayTimezone.trim() || UTC_TIMEZONE.id;
  const colors: MbscCalendarColor[] = [];

  let cursor = dayjs(visibleStart).tz(displayTz).startOf('day');
  const last = dayjs(visibleEnd).tz(displayTz).startOf('day');

  while (!cursor.isAfter(last, 'day')) {
    if (isDisplayDayInBlackoutRange(cursor, blackoutRanges, auditWindow)) {
      colors.push({
        ...blackoutDayStyle,
        date: cursor.toDate(),
        title,
      });
    }
    cursor = cursor.add(1, 'day');
  }

  return colors;
}

export interface BuildTimelineCalendarColorsArgs {
  /**
   * Recurring weekend color (highest precedence). Without `resources` it applies to every row
   * as-is; with them, it is the style of each row's country-specific non-working days.
   */
  weekendColor: MbscCalendarColor;
  /** Per-day colors for scheme blackout days inside the audit window. */
  blackoutDayColors?: MbscCalendarColor[];
  /** Per-day colors for columns outside the audit window (apply to every row). */
  outsideAuditWindowColors: MbscCalendarColor[];
  /** Per-day, per-resource time-off / holiday colors. */
  timeoffColors: MbscCalendarColor[];
  /** Optional today-highlight color (no title); appended unchanged. */
  todayColor?: MbscCalendarColor;
  /** Display timezone used for the weekend / audit-window day checks. */
  displayTimezone: string | null | undefined;
  /** Timeline rows (inspectors). With `resolveNonWorkingDays`, each row gets its country's weekend. */
  resources?: { id: string | number; countryCode?: string | null }[];
  /** Country → non-working weekdays (0 = Sunday), from the working-days config. */
  resolveNonWorkingDays?: NonWorkingDaysResolver;
}

function mbscCalendarColorDay(date: MbscCalendarColor['date'], timezoneId: string): string {
  if (typeof date === 'string') {
    return date.slice(0, 10);
  }
  if (date instanceof Date) {
    return dayjs(date).tz(timezoneId).format('YYYY-MM-DD');
  }
  return '';
}

/** Weekday (0 = Sunday) of a `YYYY-MM-DD` calendar day, or null when there is no day. */
function calendarDayOfWeek(day: string, timezoneId: string): number | null {
  return day ? dayjs.tz(`${day}T12:00:00`, timezoneId).day() : null;
}

/**
 * Assembles the schedule timeline's muted-day colors so each (date, resource) cell carries at most
 * one label. Mobiscroll renders the `title` of every color that covers a cell, so overlapping
 * unavailability reasons would otherwise stack and become illegible. Precedence is
 * weekend > outside audit window > time off. Suppressed cells keep their grey tint because the
 * winning column-level color already covers the whole day.
 *
 * With `resources` + `resolveNonWorkingDays`, the weekend follows each row's country working week
 * (e.g. Fri/Sat for a Sun–Thu country) instead of the default Sat/Sun.
 */
export function buildTimelineCalendarColors({
  weekendColor,
  blackoutDayColors = [],
  outsideAuditWindowColors,
  timeoffColors,
  todayColor,
  displayTimezone,
  resources,
  resolveNonWorkingDays,
}: BuildTimelineCalendarColorsArgs): MbscCalendarColor[] {
  const tz = displayTimezone?.trim() || UTC_TIMEZONE.id;

  const rows =
    resolveNonWorkingDays && resources?.length
      ? resources.map((row) => ({
          id: row.id,
          nonWorkingDays: resolveNonWorkingDays(row.countryCode),
        }))
      : null;
  const perRow = rows !== null;
  const nonWorkingDaysByResource = new Map<string, number[]>(
    (rows ?? []).map((row) => [String(row.id), row.nonWorkingDays])
  );
  const rowNonWorkingDays = Array.from(nonWorkingDaysByResource.values());

  // Column-level labels (every row) give way to the weekend only when it is off for every row.
  const isNonWorkingForAllRows = (day: string): boolean => {
    const weekday = calendarDayOfWeek(day, tz);
    if (weekday === null) return false;
    if (!perRow) return DEFAULT_NON_WORKING_DAYS.includes(weekday);
    return rowNonWorkingDays.every((days) => days.includes(weekday));
  };
  const isNonWorkingForRow = (day: string, resource: MbscCalendarColor['resource']): boolean => {
    const weekday = calendarDayOfWeek(day, tz);
    if (weekday === null) return false;
    const days =
      (resource != null && nonWorkingDaysByResource.get(String(resource))) ||
      DEFAULT_NON_WORKING_DAYS;
    return days.includes(weekday);
  };

  const blackoutColors = blackoutDayColors.filter(
    (color) => !isNonWorkingForAllRows(mbscCalendarColorDay(color.date, tz))
  );

  const blackoutDayKeys = new Set(
    blackoutColors
      .map((color) => mbscCalendarColorDay(color.date, tz))
      .filter((day) => day.length > 0)
  );

  // Weekend wins over blackout and outside-audit-window: drop those labels on weekend days.
  const outsideWindowColors = outsideAuditWindowColors
    .filter((color) => !isNonWorkingForAllRows(mbscCalendarColorDay(color.date, tz)))
    .filter((color) => !blackoutDayKeys.has(mbscCalendarColorDay(color.date, tz)));

  const outsideWindowDayKeys = new Set(
    outsideWindowColors
      .map((color) => mbscCalendarColorDay(color.date, tz))
      .filter((day) => day.length > 0)
  );
  const timeoffColorsSingleLabel = timeoffColors.filter((color) => {
    const day = mbscCalendarColorDay(color.date, tz);
    if (!day) {
      return true;
    }
    // Time off on the row's weekend: only the weekend label shows there.
    const { kind } = color as MbscCalendarColor & { kind?: string };
    if ((kind === 'timeoff' || kind === 'merged') && isNonWorkingForRow(day, color.resource)) {
      return false;
    }
    return !outsideWindowDayKeys.has(day) && !blackoutDayKeys.has(day);
  });

  return [
    ...buildWeekendColors(weekendColor, rows),
    ...blackoutColors,
    ...outsideWindowColors,
    ...timeoffColorsSingleLabel,
    ...(todayColor ? [todayColor] : []),
  ];
}

/** One recurring weekend color per distinct working week, scoped to the rows that share it. */
function buildWeekendColors(
  weekendColor: MbscCalendarColor,
  rows: { id: string | number; nonWorkingDays: number[] }[] | null
): MbscCalendarColor[] {
  if (!rows) {
    return [weekendColor];
  }
  const resourcesByWeekDays = new Map<string, (string | number)[]>();
  rows.forEach(({ id, nonWorkingDays }) => {
    if (nonWorkingDays.length === 0) return;
    const weekDays = toMobiscrollWeekDays(nonWorkingDays);
    resourcesByWeekDays.set(weekDays, [...(resourcesByWeekDays.get(weekDays) ?? []), id]);
  });
  return Array.from(resourcesByWeekDays, ([weekDays, resourceIds]) => ({
    ...weekendColor,
    recurring: { repeat: 'weekly', weekDays },
    resource: resourceIds,
  }));
}

function activityDateRange(
  activity: ActivityDTO,
  timezoneId: string
): { start: Dayjs; end: Dayjs } | null {
  const start = parseActivityDay(activity.startDate, timezoneId);
  const end = parseActivityDay(activity.dueDate, timezoneId);

  if (start && end) {
    return start.isAfter(end) ? { start: end, end: start } : { start, end };
  }
  if (start) return { start, end: start };
  if (end) return { start: end, end };
  return null;
}

/**
 * Date the schedule calendar should open to when planning a plan box manually:
 * start of the mutual overlap across activity [startDate, dueDate] windows,
 * or the earliest start date when there is no overlap.
 */
/** Calendar date to open when the user selects a plan-box activity (start of audit window). */
export function scheduleNavigateDateFromActivity(
  activity: ActivityDTO | null | undefined,
  fallbackTimezoneId: string | null | undefined = UTC_TIMEZONE.id
): Date | undefined {
  const window = activityAuditWindowFromActivity(activity, fallbackTimezoneId);
  return window?.start.toDate();
}

export function scheduleStartDateFromActivities(activities: ActivityDTO[]): Date | undefined {
  const ranges = activities
    .map((activity) => activityDateRange(activity, UTC_TIMEZONE.id))
    .filter((range): range is { start: Dayjs; end: Dayjs } => range != null);

  if (ranges.length === 0) return undefined;

  let overlapStart = ranges[0].start;
  let overlapEnd = ranges[0].end;

  for (let i = 1; i < ranges.length; i++) {
    const { start, end } = ranges[i];
    overlapStart = overlapStart.isAfter(start) ? overlapStart : start;
    overlapEnd = overlapEnd.isBefore(end) ? overlapEnd : end;
  }

  if (!overlapStart.isAfter(overlapEnd)) {
    return overlapStart.toDate();
  }

  let earliestStart = ranges[0].start;
  for (let i = 1; i < ranges.length; i++) {
    if (ranges[i].start.isBefore(earliestStart)) {
      earliestStart = ranges[i].start;
    }
  }
  return earliestStart.toDate();
}
